<div
  *ngIf="show"
  class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4 overflow-y-auto"
>
  <div
    class="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 my-8 max-h-[90vh] flex flex-col"
  >
    <!-- Header -->
    <div class="flex justify-between items-center pb-4 border-b border-gray-100 mb-4">
      <div>
        <h2 class="text-xl font-bold text-gray-900">Convert to NOAH B2B Account</h2>
        <p class="text-xs text-gray-500 mt-0.5">
          Convert a target user to a B2B Business account by providing required company and address information.
        </p>
      </div>
      <button
        type="button"
        (click)="onClose()"
        class="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Error Banner -->
    <div
      *ngIf="errorMessage"
      class="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-md flex items-start space-x-2 text-xs text-red-700"
    >
      <svg
        class="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>{{ errorMessage }}</span>
    </div>

    <!-- Form Content -->
    <form (ngSubmit)="onSubmit()" class="overflow-y-auto flex-1 pr-1 space-y-6">
      <!-- Section 1: Business Details -->
      <div>
        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          1. Business Details
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="col-span-full md:col-span-1">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Registered Name / Raison Sociale <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="registered_name"
              name="registered_name"
              required
              placeholder="e.g. Acme Global LLC"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>

          <div class="col-span-full md:col-span-1">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Registration Number <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="registration_number"
              name="registration_number"
              required
              placeholder="e.g. REG-889900"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>

          <div class="col-span-full md:col-span-1">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Tax Identification Number <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="tax_identification_number"
              name="tax_identification_number"
              required
              placeholder="e.g. FR12345678901 / TIN-12345"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>

          <div class="col-span-full md:col-span-1">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Entity Type <span class="text-red-500">*</span>
            </label>
            <select
              [(ngModel)]="entity_type"
              name="entity_type"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50 bg-white"
            >
              <option *ngFor="let option of entityTypes" [value]="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="col-span-full md:col-span-1">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Registration Country <span class="text-red-500">*</span>
            </label>
            <select
              [ngModel]="registration_country"
              (ngModelChange)="onRegistrationCountryChange($event)"
              name="registration_country"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="" disabled selected>Select Country</option>
              <option *ngFor="let c of countries" [value]="c.code">
                {{ c.name }} ({{ c.code }})
              </option>
            </select>
          </div>

          <div class="col-span-full md:col-span-1">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Incorporation Date <span class="text-red-500">*</span>
            </label>
            <input
              type="date"
              [(ngModel)]="incorporation_date"
              name="incorporation_date"
              [max]="maxDate"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>
        </div>
      </div>

      <!-- Section 2: Registered Address -->
      <div>
        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          2. Registered Address
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="col-span-full">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Street <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="street"
              name="street"
              required
              placeholder="e.g. 10 Rue de la Paix"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">
              City <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="city"
              name="city"
              required
              placeholder="e.g. Paris"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">
              State <span class="text-red-500">*</span>

            </label>
            <input
              type="text"
              [(ngModel)]="state"
              name="state"
              required
              placeholder="e.g. IDF"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Postal Code / ZIP <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="postal_code"
              name="postal_code"
              required
              placeholder="e.g. 75002"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:opacity-50"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Country <span class="text-red-500">*</span>
            </label>
            <select
              [ngModel]="country"
              (ngModelChange)="onCountryChange($event)"
              name="country"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="" disabled selected>Select Country</option>
              <option *ngFor="let c of countries" [value]="c.code">
                {{ c.name }} ({{ c.code }})
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="pt-4 border-t border-gray-100 flex justify-end space-x-3">
        <button
          type="button"
          (click)="onClose()"
          [disabled]="isSubmitting"
          class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs font-medium transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          [disabled]="isSubmitting"
          class="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium transition disabled:opacity-50 flex items-center space-x-2"
        >
          <div
            *ngIf="isSubmitting"
            class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"
          ></div>
          <span>{{ isSubmitting ? 'Creating...' : 'Submit' }}</span>
        </button>
      </div>
    </form>
  </div>
</div>
